/**
 * HR help text for the Roster module.
 *
 * The roster decides what the system expects from an employee each day. A shift
 * gives the day its expected start and end times, which attendance then compares
 * punches against to decide late, early, short duration, and overtime. A day
 * with no shift and no rest day counts as a day off. Rest days and holidays
 * change the same day into paid-but-not-worked, so roster mistakes surface as
 * salary mistakes at the end of the month.
 */

const shift = {
  date: "The work date on which this shift will be assigned to the employee. Attendance for that day is compared against this shift's timing.",
  days: "Create shifts only on these weekdays within the selected start and end dates.",
  time_slot_id: "Select the working schedule for this shift, including its start and end times, attendance buffers, and breaks. Overlapping shifts for the same employee are not allowed.",
  is_full_overtime_shift: "Treat the whole shift as overtime, so every worked minute is paid through the overtime policy instead of counting as normal duty. Use it for a call-out or an extra day worked.",
  employment_id: "Employee this shift belongs to.",
  start_date: "The first date on which shifts will be created for the selected employees.",
  end_date: "The last date of the shift creation period. Leave it the same as the start date for a single-day roster.",
};

const timeSlot = {
  name: "Name of this time slot, such as Morning Shift or Night Shift. HR picks it by name when building a roster.",
  start: "The scheduled time when this shift starts.",
  end: "The scheduled time when this shift ends. If it is earlier than the start time, the shift ends on the next day.",
  start_buffer_minutes: "Grace period after the shift starts. An employee is marked late only after these minutes have passed.",
  end_buffer_minutes: "Grace period before the shift ends. An employee is marked as leaving early only when they leave before this period begins.",
  punch_start_buffer_minutes: "How many minutes before shift start the system can accept a punch for this shift.",
  punch_end_buffer_minutes: "How many minutes after shift end the system can accept a punch for this shift.",
  is_overtime_applicable: "Allow eligible extra time worked around this shift to be calculated as overtime, according to attendance settings.",
  is_full_overtime_slot: "Treat all time worked in this time slot as overtime.",
  is_flexible: "Use required working hours instead of fixed late-arrival and early-leaving times for this slot.",
  flexible_minutes: "Total working hours required for this flexible slot. Breaks marked as not inclusive are deducted from the required time.",
  is_inclusive: "Include this break in paid working time. If not selected, the break duration is deducted from expected working hours.",
  is_night: "Mark this as a night shift so it can be included in night-shift payroll calculations.",
  font_colour: "Text color used to identify this time slot on roster and shift displays.",
  bg_colour: "Background color used to identify this time slot on roster and shift displays.",
};

const restDay = {
  employment_name: "Employee this rest day belongs to.",
  date: "The day marked as a rest day. The employee is not expected to attend, so the day is not treated as absent.",
  start_date: "First day of the range rest days are created in.",
  end_date: "Last day of the range rest days are created in.",
  days: "Which weekdays become rest days within the range, for example Sunday. Only the ticked days are created.",
  font_colour: "Text colour used for this rest day on the roster calendar.",
  bg_colour: "Background colour used for this rest day on the roster calendar. Pick something clearly different from working days.",
};

const holiday = {
  name: "Name of the holiday as employees will see it on the roster calendar.",
  start: "First day of the holiday.",
  end: "Last day of the holiday. Use the same date as the start for a single-day holiday.",
  description: "Internal note about this holiday.",
  is_attendance_exempt: "Employees who do attend on this holiday are exempted from the usual attendance checks, so they are not marked late or short. Leave it off when the holiday should be treated like a normal working day for anyone who works it.",
};

const holidayRule = {
  name: "Name of this rule so HR can recognize which group it targets.",
  type: "What the rule matches on: religion, organization structure, department, specific employees, or geographic location. Add several rules when a holiday applies to more than one group; an employee matching any one rule gets the holiday. A holiday with no rules at all applies to everyone.",
  religion_id: "Employees of this religion get the holiday. It is matched against the religion on the employee's personal record.",
  location_id: "Employees working at this location get the holiday.",
  branch_ids: "Employees of these branches get the holiday.",
  department_ids: "Employees of these departments get the holiday.",
  employment_ids: "The specific employees who get the holiday.",
  country_id: "Employees whose present address is in this country get the holiday.",
  state_id: "Employees whose present address is in this state get the holiday.",
  city_id: "Employees whose present address is in this city get the holiday.",
};

const rosterDelete = {
  start_date: "First day whose roster will be deleted. Deleting a roster removes the expected timings for those days, so attendance for them will no longer be judged against a shift.",
  end_date: "Last day whose roster will be deleted.",
  delete_all_employees: "Delete the roster for every employee in this range. Turn it off to delete for a selected group only.",
  employment_id: "Employees whose roster will be deleted.",
  grade_id: "Delete the roster for employees of these grades.",
  location_id: "Delete the roster for employees at these locations.",
  department_id: "Delete the roster for employees in these departments.",
  designation_id: "Delete the roster for employees with these designations.",
  branch_id: "Delete the roster for employees of these branches.",
};

const importRoster = {
  file_attachment: "Excel file of shifts to import. Use the sample file so the employee code, date, and time slot columns match; rows whose time slot does not already exist are rejected.",
};

export const rosterTooltips = {
  shift,
  timeSlot,
  restDay,
  holiday,
  holidayRule,
  rosterDelete,
  importRoster,
};

export default rosterTooltips;
